<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Menu from 'primevue/menu';
import Tag from 'primevue/tag';
import { useActivities, useDateFns, useStringFunctions, useUtilities } from '@apparatix/composables';
import { Noir } from '@apparatix/theme/Noir';
import type { Activity, ActivityMethodName, ActivityTypeName } from '@apparatix/schemas/Activities';
import type { TypographyVariant } from '@apparatix/library/core';
import { Toolbar, Typography } from '@apparatix/library/core';
import FileViewerButton from './FileViewerButton.vue';
import ActivityItemIcon from './ActivityItemIcon.vue';
import NoteViewer from './NoteViewer.vue';
import GotoRelationshipButton from '../GotoRelationshipButton.vue';
import RadioFor from '@apparatix/library/RadioFor.vue';
import { ActivityItemProps } from './commonProps';
import { MenuItem } from 'primevue/menuitem';
import EmailThreadViewer from './EmailThreadViewer.vue';
import CheckIcon from '@primeicons/vue/check';
import PenToSquareIcon from '@primeicons/vue/pen-to-square';
import TrashIcon from '@primeicons/vue/trash';
import StopwatchIcon from '@primeicons/vue/stopwatch';
import EllipsisHIcon from '@primeicons/vue/ellipsis-h';

type Emits = {
  (e: 'completed-change', completed: boolean, activity: Activity): void,
  (e: 'edit', activity: Activity): void,
  (e: 'delete', activityId: number, series: boolean): void,
  (e: 'defer', activity: Activity): void,
};

const props = defineProps<ActivityItemProps>();
const emit = defineEmits<Emits>();

const {
  arrayHasValues,
  asKeyedItems,
  combineArrays,
  insertArrayItemAtIndex,
} = useUtilities();

const { formatDate } = useDateFns();
const { isEmptyString } = useStringFunctions();

const {
  getDisplayType,
  getEmailActivityTitle,
  hasRelationships,
  isActivity,
  isEmailActivity,
  isPartOfSeries,
  isReminder,
  shouldDisableField,
} = useActivities();

const menuRef = ref();

const isComplete = computed({
  get() {
    return Boolean(props.activity?.completedAt);
  },
  set(completed) {
    emit('completed-change', completed, props.activity);
  },
});

const relationships = computed(() => {
  if (!hasRelationships(props.activity)) return undefined;

  return asKeyedItems(
    combineArrays(
      props.activity.accounts,
      props.activity.campaigns,
      props.activity.contacts,
    )
      .filter(r => r.id !== props.parentId),
  );
});

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [
    {
      // Below is maintained logic but reverted verbiage based on pre-existing verbiage
      // Requires future discussion for verbiage
      label: props.activity?.completedAt ? 'Mark as complete' : 'Mark as complete',
      icon: CheckIcon,
      disabled: () => shouldDisableField('completed', props.activity),
      command: () => emit('completed-change', !props.activity.completedAt, props.activity),
    },
    { separator: true },
    {
      label: 'Edit',
      icon: PenToSquareIcon,
      command: () => emit('edit', props.activity),
    },
    {
      label: 'Delete',
      icon: TrashIcon,
      disabled: () => shouldDisableField('delete', props.activity),
      command: () => emit('delete', props.activity.id, false),
    },
  ];

  if (isReminder(props.activity)) {
    insertArrayItemAtIndex(
      { label: 'Defer', icon: StopwatchIcon, command: () => emit('defer', props.activity) },
      1,
      items,
    );
  }

  if (isPartOfSeries(props.activity)) {
    items.push({
      label: 'Delete series',
      icon: TrashIcon,
      disabled: () => shouldDisableField('delete', props.activity),
      command: () => emit('delete', props.activity.id, true),
    });
  }

  return items;
});

const subtitleOptions = computed<{ shouldShow: boolean, value?: string, variant?: TypographyVariant, class?: string }>(() => {
  if (!isEmailActivity(props.activity)) {
    return {
      shouldShow: true,
      value: formatDate(props.activity.startDateTime, 'MMMM do, yyyy', ''),
      variant: 'section-subheading',
    };
  }

  const subject = getEmailActivityTitle(props.activity);

  if (isEmptyString(subject, true) || subject.toLowerCase() === props.activity.title?.toLowerCase()) {
    return { shouldShow: false };
  }

  return {
    shouldShow: true,
    value: subject,
    variant: 'email-thread-subject',
    class: 'capitalize',
  };
});
</script>
<template>
  <div class="flex flex-col lg:flex-row gap-4 w-full">
    <ActivityItemIcon v-if="isActivity(props.activity)" :method="(props.activity.method?.name as ActivityMethodName)" />
    <ActivityItemIcon v-else :type="(props.activity.type.name as ActivityTypeName)" />
    <section class="flex flex-col rounded-lg bg-[var(--white)] grow" style="box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)">
      <div class="flex items-start lg:items-center justify-between p-6">
        <div class="flex items-center gap-2 flex-wrap grow">
          <Typography v-if="props.activity?.title" variant="activity-heading">
            {{ props.activity.title }}
          </Typography>
          <Typography v-else variant="activity-heading">
            {{ getDisplayType(props.activity) }}
          </Typography>
          <Tag
            v-if="props.activity.purpose"
            severity="secondary"
            style="text-wrap: nowrap;"
            :dt="Noir.tag"
            :value="props.activity.purpose.displayName"
          />
        </div>
        <Toolbar>
          <div class="hidden lg:flex items-center gap-2 justify-end">
            <slot name="headerButtons" />
          </div>
          <Button
            icon-only
            variant="text"
            aria-label="Open menu"
            :dt="Noir.button"
            rounded
            @click="e => menuRef.toggle(e)"
          >
            <EllipsisHIcon />
          </Button>
        </Toolbar>
      </div>
      <div v-if="subtitleOptions.shouldShow || arrayHasValues(props.activity.files)" class="flex items-center justify-between px-6 pb-6">
        <Typography v-if="subtitleOptions.shouldShow" :variant="subtitleOptions.variant" :class="subtitleOptions.class">
          {{ subtitleOptions.value }}
        </Typography>
        <FileViewerButton v-if="arrayHasValues(props.activity.files)" :files="props.activity.files" />
      </div>
      <div v-if="arrayHasValues(props.activity.emails)" class="w-full">
        <EmailThreadViewer :emails="props.activity.emails" />
      </div>
      <div v-if="arrayHasValues(props.activity.notes)" class="flex p-6 bg-[var(--apx-note-background-color)]">
        <NoteViewer :note-html="props.activity.notes[0].note" />
      </div>
      <div v-if="relationships" class="flex flex-row flex-wrap gap-2 px-6 py-4">
        <GotoRelationshipButton
          v-for="relationship in relationships"
          :key="relationship.key"
          :relationship="relationship"
          :keep-current-parent="props.relationshipButtonsKeepParent"
        />
      </div>
      <div class="flex items-center justify-between px-6 py-4 border-t border-[var(--p-content-border-color)]">
        <Typography variant="section-footer" muted>
          Created by {{ props.activity.createdBy.fullName }} on {{ formatDate(props.activity.createdAt, 'MMMM do, yyyy', '') }}
        </Typography>
        <Toolbar align="left" class="collapse lg:visible">
          <!-- Below is maintained logic but reverted verbiage based on pre-existing verbiage -->
          <!-- Requires future discussion for verbiage -->
          <RadioFor :label="props.activity.completedAt ? 'Mark as complete' : 'Mark as complete'">
            <template #default="{id}">
              <Checkbox
                v-model="isComplete"
                :input-id="id"
                :disabled="shouldDisableField('completed', props.activity)"
                binary
              />
            </template>
          </RadioFor>
        </Toolbar>
      </div>
    </section>
  </div>
  <Menu ref="menuRef" :model="menuItems" popup />
</template>
